/**
 * Performance monitoring utilities for tracking and improving app performance
 */

import { useEffect, useRef } from 'react';

// Constants for performance thresholds
const RENDER_THRESHOLD_WARNING = 16; // 60fps threshold (ms)
const RENDER_THRESHOLD_SEVERE = 50; // Severe render time (ms)
const QUERY_THRESHOLD_WARNING = 1000; // 1 second query time (ms)
const QUERY_THRESHOLD_SEVERE = 3000; // 3 seconds query time (ms)
const EXCESSIVE_RENDERS = 5; // Number of renders considered excessive
const NAVIGATION_THRESHOLD_WARNING = 300; // 300ms navigation threshold (feels sluggish)
const NAVIGATION_THRESHOLD_SEVERE = 1000; // 1 second navigation threshold (feels broken)

/**
 * Measure component render time
 * @param componentName Name of the component being measured
 * @param callback Function to execute and measure
 * @returns Result of the callback function
 */
export function measureRenderTime<T>(
  componentName: string,
  callback: () => T,
): T {
  if (!import.meta.env.DEV) {
    return callback();
  }

  const startTime = performance.now();
  const result = callback();
  const endTime = performance.now();
  const duration = endTime - startTime;


  // Log warning for slow renders
  if (duration > RENDER_THRESHOLD_SEVERE) {
    console.error(
      `🚨 Critically slow render in ${componentName}: ${duration.toFixed(2)}ms - Consider code splitting or optimization`,
    );
  } else if (duration > RENDER_THRESHOLD_WARNING) {
    console.warn(
      `🐢 Slow render detected in ${componentName}: ${duration.toFixed(2)}ms - Check for unnecessary re-renders`,
    );
  }

  return result;
}

/**
 * Track React Query performance
 * @param queryKey The query key to track
 * @param duration Duration in milliseconds
 */
export function trackQueryPerformance(
  queryKey: unknown[],
  duration: number,
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const queryName = Array.isArray(queryKey)
    ? queryKey.join('/')
    : String(queryKey);


  // Log warning for slow queries
  if (duration > QUERY_THRESHOLD_SEVERE) {
    console.error(
      `⚠️ Critical query performance issue [${queryName}]: ${duration.toFixed(2)}ms - Consider server-side optimization or caching`,
    );
  } else if (duration > QUERY_THRESHOLD_WARNING) {
    console.warn(
      `🐌 Slow query detected [${queryName}]: ${duration.toFixed(2)}ms - Check network or query complexity`,
    );
  }
}

/**
 * Track navigation performance
 * @param path The route path
 * @param duration Duration in milliseconds from user action to completed render
 */
export function trackNavigationPerformance(
  path: string,
  duration: number,
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  // Safe version that doesn't rely on performance marks that might get cleared

  // Don't warn for very short navigations (probably cache hits)
  if (duration < 50) {
    return;
  }

  // Log warning for slow navigations
  if (duration > NAVIGATION_THRESHOLD_SEVERE) {
    console.error(
      `⚠️ Critically slow navigation to ${path}: ${duration.toFixed(2)}ms - Consider code splitting, preloading, or optimizing the page component`,
    );
  } else if (duration > NAVIGATION_THRESHOLD_WARNING) {
    console.warn(
      `🐌 Slow navigation detected to ${path}: ${duration.toFixed(2)}ms - Check for expensive operations during mount`,
    );
  }

  // Always safely record this as a custom metric without relying on existing marks
  try {
    // Create marks just for this navigation
    const safeMarkPrefix = `nav-${Date.now()}`;
    performance.mark(`${safeMarkPrefix}-start`);
    performance.mark(`${safeMarkPrefix}-end`);
    performance.measure(
      `${safeMarkPrefix}-measure`,
      `${safeMarkPrefix}-start`,
      `${safeMarkPrefix}-end`,
    );

    // Clean up immediately to avoid memory leaks
    performance.clearMarks(`${safeMarkPrefix}-start`);
    performance.clearMarks(`${safeMarkPrefix}-end`);
    performance.clearMeasures(`${safeMarkPrefix}-measure`);
  } catch (error) {
    // Silent fail for browsers that don't support performance API
  }
}

/**
 * Hook to monitor component re-renders
 * @param componentName Name of the component to monitor
 * @returns Render count for development purposes
 */
export function useRenderCount(componentName: string): number {
  const renderCount = useRef(0);
  renderCount.current += 1;

  // Use useEffect to log after render is complete
  useEffect(() => {
    if (import.meta.env.DEV && renderCount.current > EXCESSIVE_RENDERS) {
      console.warn(
        `♻️ Excessive re-renders detected in ${componentName}: ${renderCount.current} renders - Check memoization and state updates`,
      );
    }
  }, [componentName]);

  return renderCount.current;
}

/**
 * Create a performance mark for web vitals tracking
 * @param markName Name of the mark to create
 */
export function markPerformance(markName: string): void {
  if (typeof performance === 'undefined') return;

  try {
    performance.mark(markName);
  } catch (error) {
    console.error(`Failed to create performance mark: ${markName}`, error);
  }
}

/**
 * Measure time between two performance marks
 * @param startMark Starting mark name
 * @param endMark Ending mark name
 * @param measureName Name for this measurement
 * @returns Duration in milliseconds
 */
export function measurePerformance(
  startMark: string,
  endMark: string,
  measureName: string,
): number {
  if (typeof performance === 'undefined') return 0;

  try {
    // Check if both marks exist before measuring
    const allMarks = performance
      .getEntriesByType('mark')
      .map((mark) => mark.name);
    const hasStartMark = allMarks.includes(startMark);
    const hasEndMark = allMarks.includes(endMark);

    if (!hasStartMark || !hasEndMark) {
      // If marks are missing, log a debug message and return 0
      if (import.meta.env.DEV) {
        console.debug(
          `Can't measure performance: ${measureName} - Missing marks.`,
          !hasStartMark ? `Start mark '${startMark}' is missing.` : '',
          !hasEndMark ? `End mark '${endMark}' is missing.` : '',
        );
      }
      return 0;
    }

    // Proceed with measurement if both marks exist
    performance.measure(measureName, startMark, endMark);
    const entries = performance.getEntriesByName(measureName);
    const lastEntry = entries[entries.length - 1];
    return lastEntry?.duration || 0;
  } catch (error) {
    console.error(`Failed to measure performance: ${measureName}`, error);
    return 0;
  }
}

/**
 * Track React useTransition performance
 * @param transitionName Name of the transition
 * @param duration Duration in milliseconds
 */
export function trackTransitionPerformance(
  transitionName: string,
  duration: number,
): void {
  if (!import.meta.env.DEV) {
    return;
  }


  if (duration > 300) {
    console.warn(
      `⏳ Slow transition detected [${transitionName}]: ${duration.toFixed(2)}ms - Consider optimizing or showing a loading indicator`,
    );
  }
}

/**
 * Hook to track interaction timing
 * @param interactionName Name of the interaction
 */
export function useInteractionTracking(interactionName: string) {
  const startTimeRef = useRef<number | null>(null);

  const startTracking = () => {
    startTimeRef.current = performance.now();
  };

  const endTracking = () => {
    if (startTimeRef.current === null) return 0;

    const duration = performance.now() - startTimeRef.current;

    if (import.meta.env.DEV) {

      if (duration > 100) {
        console.warn(
          `🐢 Slow interaction detected [${interactionName}]: ${duration.toFixed(2)}ms - Consider optimizing event handlers`,
        );
      }
    }

    startTimeRef.current = null;
    return duration;
  };

  return { startTracking, endTracking };
}

/**
 * Track core web vitals and custom metrics
 * Can be used to initialize monitoring for important user-centric metrics
 */
export function initPerformanceMonitoring(): void {
  if (typeof window === 'undefined' || !import.meta.env.PROD) return;

  // Report Web Vitals
  try {
    // Example implementation for capturing web vitals
    if ('onCLS' in window) {
      // Note: This is a placeholder - you would need to install web-vitals package
      // import { onCLS, onFID, onLCP } from 'web-vitals';
      // onCLS(console.log);
      // onFID(console.log);
      // onLCP(console.log);
    }
  } catch (error) {
    console.error('Failed to initialize performance monitoring', error);
  }
}

/**
 * Create a custom performance metric
 * @param metricName Name of the custom metric
 * @param value Value of the metric
 */
export function reportCustomMetric(metricName: string, _value: number): void {
  if (typeof performance === 'undefined' || !performance.mark) return;

  try {
    // Create a performance mark
    const markName = `metric_${metricName}`;
    performance.mark(markName);

    // Store as a performance entry if possible
    if (performance.setResourceTimingBufferSize) {
      performance.setResourceTimingBufferSize(500); // Ensure we have enough buffer
    }

    // Log metric in development
    if (import.meta.env.DEV) {
    }

    // In production, you could send this to your analytics service
    // if (import.meta.env.PROD && window.analytics) {
    //   window.analytics.track('Performance Metric', {
    //     name: metricName,
    //     value: value,
    //     timestamp: new Date().toISOString()
    //   });
    // }
  } catch (error) {
    console.error(`Failed to report custom metric: ${metricName}`, error);
  }
}

/**
 * Reset all performance measurements to keep memory usage in check
 * This preserves certain critical performance marks used for initial load measurement
 */
export function resetPerformanceMeasurements(): void {
  if (typeof performance === 'undefined') return;

  try {
    // Get all current marks
    const marks = performance.getEntriesByType('mark');

    // Clear all measures - we'll recreate the ones we need
    if (performance.clearMeasures) {
      performance.clearMeasures();
    }

    // Clear all marks except the critical ones for app bootstrapping
    if (performance.clearMarks) {
      // First clear all marks
      performance.clearMarks();

      // Then recreate the critical ones if they existed
      const criticalMarks = [
        'app-bootstrap-start',
        'app-bootstrap-end',
        'app-init',
      ];

      // Recreate any critical marks that existed before
      marks.forEach((mark) => {
        if (criticalMarks.includes(mark.name)) {
          performance.mark(mark.name);
        }
      });
    }

    // Clear resource timings to keep memory usage in check
    if (performance.clearResourceTimings) {
      performance.clearResourceTimings();
    }
  } catch (error) {
    console.error('Failed to clear performance measurements', error);
  }
}
